import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box } from '@mui/material';

/**
 * ScrollShadow - A container that shows dynamic shadows to indicate scrollable content
 * @param {string} orientation - 'vertical' or 'horizontal'
 * @param {number} shadowSize - Size of the shadow in pixels
 * @param {React.ReactNode} children - Content to render inside
 * @param {object} sx - Additional MUI sx styles
 */
const ScrollShadow = ({
  orientation = 'vertical',
  shadowSize = 24,
  children,
  sx = {},
  ...props
}) => {
  const scrollRef = useRef(null);
  const [showStartShadow, setShowStartShadow] = useState(false);
  const [showEndShadow, setShowEndShadow] = useState(false);

  const updateShadows = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;

    if (orientation === 'vertical') {
      const canScrollUp = el.scrollTop > 1;
      const canScrollDown = el.scrollTop < el.scrollHeight - el.clientHeight - 1;
      setShowStartShadow(canScrollUp);
      setShowEndShadow(canScrollDown);
    } else {
      const canScrollLeft = el.scrollLeft > 1;
      const canScrollRight = el.scrollLeft < el.scrollWidth - el.clientWidth - 1;
      setShowStartShadow(canScrollLeft);
      setShowEndShadow(canScrollRight);
    }
  }, [orientation]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    // Initial check with small delay for content to render
    updateShadows();
    const timer = setTimeout(updateShadows, 100);

    // Check on resize of container and content
    const resizeObserver = new ResizeObserver(updateShadows);
    resizeObserver.observe(el);

    // Also observe children for size changes
    Array.from(el.children).forEach((child) => {
      resizeObserver.observe(child);
    });

    return () => {
      clearTimeout(timer);
      resizeObserver.disconnect();
    };
  }, [updateShadows, children]);

  const isVertical = orientation === 'vertical';

  const shadowStyles = {
    content: '""',
    position: 'absolute',
    pointerEvents: 'none',
    zIndex: 1,
    transition: 'opacity 0.2s ease',
    ...(isVertical
      ? { left: 0, right: 0, height: `${shadowSize}px` }
      : { top: 0, bottom: 0, width: `${shadowSize}px` }),
  };

  return (
    <Box
      sx={{
        position: 'relative',
        overflow: 'hidden',
        ...sx,
      }}
      {...props}
    >
      {/* Start shadow (top or left) */}
      <Box
        sx={{
          ...shadowStyles,
          opacity: showStartShadow ? 1 : 0,
          ...(isVertical
            ? { top: 0, background: 'linear-gradient(to bottom, rgba(0,0,0,0.12) 0%, rgba(0,0,0,0) 100%)' }
            : { left: 0, background: 'linear-gradient(to right, rgba(0,0,0,0.12) 0%, rgba(0,0,0,0) 100%)' }),
        }}
      />
      {/* End shadow (bottom or right) */}
      <Box
        sx={{
          ...shadowStyles,
          opacity: showEndShadow ? 1 : 0,
          ...(isVertical
            ? { bottom: 0, background: 'linear-gradient(to top, rgba(0,0,0,0.12) 0%, rgba(0,0,0,0) 100%)' }
            : { right: 0, background: 'linear-gradient(to left, rgba(0,0,0,0.12) 0%, rgba(0,0,0,0) 100%)' }),
        }}
      />
      {/* Scrollable content */}
      <Box
        ref={scrollRef}
        data-scroll-container
        onScroll={updateShadows}
        sx={{
          width: '100%',
          height: '100%',
          overflowX: isVertical ? 'hidden' : 'auto',
          overflowY: isVertical ? 'auto' : 'hidden',
          scrollbarWidth: 'none',
          '&::-webkit-scrollbar': { display: 'none' },
          WebkitOverflowScrolling: 'touch',
        }}
      >
        {children}
      </Box>
    </Box>
  );
};

export default ScrollShadow;
